import React, { createContext, useState, useEffect } from 'react';
import { requestGetHabitatsLevel1, requestGetHabitatsLevel2, requestGetHabitatsLevel3 } from 'api/siteHabitat';

export const HabitatContext = createContext();

export const HabitatProvider = ({ children }) => {
    const [level1Habitats, setLevel1Habitats] = useState([]);
    const [level2Habitats, setLevel2Habitats] = useState([]);
    const [level3Habitats, setLevel3Habitats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchHabitatLevels = async () => {
            try {
                const lvl1 = await requestGetHabitatsLevel1();
                const lvl2 = await requestGetHabitatsLevel2();
                const lvl3 = await requestGetHabitatsLevel3();

                setLevel1Habitats(lvl1.level1Habitats || []);
                setLevel2Habitats(lvl2.level2Habitats || []);
                setLevel3Habitats(lvl3.level3Habitats || []);

                setLoading(false);
            } catch (error) {
                setError(error);
                setLoading(false);
            }
        };

        fetchHabitatLevels();
    }, []);

    const deleteHabitat = (habitatId) => {
        setLevel3Habitats(level3Habitats.filter(habitat => habitat.value !== habitatId));
        setLevel2Habitats(level2Habitats.filter(habitat => habitat.value !== habitatId));
        setLevel1Habitats(level1Habitats.filter(habitat => habitat.value !== habitatId));
    };

    // Rendering loading state while fetching data
    if (loading) {
        return <div>Loading...</div>;
    }

    // Rendering error state if there's an error during data fetching
    if (error) {
        return <div>Error: {error.message}</div>;
    }

    // Calculating formatted habitats
    const formattedHabitatsLevelOne = level1Habitats.map((item) => ({
        value: item.value,
        label: item.label
    }));

    const formattedHabitatsLevelTwo = level2Habitats.map((item) => ({
        value: item.value,
        label: item.label,
        Level1HabitatId: item.Level1HabitatId
    }));

    const formattedHabitatsLevelThree = level3Habitats.map((item) => ({
        value: item.value,
        label: item.label,
        Level1HabitatId: item.Level1HabitatId,
        Level2HabitatId: item.Level2HabitatId
    }));

    const defaultHabitatsOptions = {
        level1Habitats: formattedHabitatsLevelOne,
        level2Habitats: formattedHabitatsLevelTwo,
        level3Habitats: formattedHabitatsLevelThree
    };

    // Function to filter available habitats options
    const filterAvailableHabitatsOptions = (habitatsList) => {
        const lvl3 = defaultHabitatsOptions.level3Habitats.filter((element) => {
            return !habitatsList.find((selected) => selected.Level3HabitatId === element.value);
        });

        const lvl2 = defaultHabitatsOptions.level2Habitats.filter((element) => {
            return (
                lvl3.some((lvl3Threads) => lvl3Threads.Level2HabitatId === element.value) ||
                !habitatsList.find((selected) => selected.Level2HabitatId === element.value)
            );
        });

        const lvl1 = defaultHabitatsOptions.level1Habitats.filter((element) => {
            return (
                lvl2.some((lvl2Threads) => lvl2Threads.Level1HabitatId === element.value) ||
                !habitatsList.find((selected) => selected.Level1HabitatId === element.value)
            );
        });

        return { level1Habitats: lvl1, level2Habitats: lvl2, level3Habitats: lvl3 };
    };

    // Providing habitat data, formatted options, delete function, and filtering function through context
    return (
        <HabitatContext.Provider
            value={{
                level1Habitats,
                level2Habitats,
                level3Habitats,
                defaultHabitatsOptions,
                deleteHabitat,
                filterAvailableHabitatsOptions
            }}
        >
            {children}
        </HabitatContext.Provider>
    );
};